import React from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Sparkle, ShieldCheck, ArrowRight, CheckCircle, Lightning } from "@phosphor-icons/react";
import SmileSimulator from "@/components/site/SmileSimulator";

export default function SmileSimulatorPage() {
    return (
        <div data-testid="smile-simulator-page">
            {/* HERO */}
            <section className="relative py-16 lg:py-24 overflow-hidden">
                <div className="absolute inset-0 -z-10 teal-radial-bg" aria-hidden="true" />
                <div className="absolute -top-32 -left-20 w-[480px] h-[480px] rounded-full bg-primary/12 blur-3xl pointer-events-none -z-10" aria-hidden="true" />
                <div className="absolute -bottom-32 -right-20 w-[420px] h-[420px] rounded-full bg-amber-200/30 blur-3xl pointer-events-none -z-10" aria-hidden="true" />

                <div className="max-w-5xl mx-auto px-5 sm:px-8 text-center">
                    <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white border border-primary/20 text-primary text-[11px] font-bold tracking-[0.2em] uppercase mb-5 shadow-sm">
                        <Sparkle size={12} weight="fill" /> AI Smile Preview
                    </div>
                    <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05]">
                        See your <span className="text-primary">new smile</span><br className="hidden sm:block" /> before you start.
                    </h1>
                    <p className="text-muted-foreground mt-6 max-w-2xl mx-auto text-base sm:text-lg">
                        Upload a clear selfie and our AI will preview your post-Invisique™ smile in 10 seconds. Free, instant, and powered by Nano Banana AI.
                    </p>

                    <div className="mt-7 flex flex-wrap justify-center gap-x-6 gap-y-2 text-sm text-muted-foreground">
                        {[
                            { icon: Lightning, text: "Results in 10 seconds" },
                            { icon: ShieldCheck, text: "100% private, no signup needed" },
                            { icon: CheckCircle, text: "No credit card, no commitment" },
                        ].map((b) => (
                            <div key={b.text} className="flex items-center gap-1.5">
                                <b.icon size={14} weight="duotone" color="hsl(192 100% 31%)" />
                                <span>{b.text}</span>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* SIMULATOR */}
            <section className="pb-16 lg:pb-20">
                <div className="max-w-3xl mx-auto px-5 sm:px-8">
                    <SmileSimulator />
                </div>
            </section>

            {/* HOW IT WORKS */}
            <section className="py-16 lg:py-20 bg-secondary/40 border-y border-border/60">
                <div className="max-w-5xl mx-auto px-5 sm:px-8">
                    <div className="text-center mb-12">
                        <div className="overline text-primary mb-3">Behind the magic</div>
                        <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight">How the AI preview works</h2>
                    </div>
                    <div className="grid md:grid-cols-3 gap-6">
                        {[
                            { step: "01", title: "Snap a selfie", desc: "Smile naturally with your teeth visible. Front-facing, good lighting, that's it." },
                            { step: "02", title: "AI does its thing", desc: "Our model precisely identifies your teeth, gently aligns them, and brightens your smile, keeping everything else exactly as it is." },
                            { step: "03", title: "Drag & reveal", desc: "Slide the handle to compare your current smile with your potential new one, side by side." },
                        ].map((s) => (
                            <div key={s.step} className="p-6 bg-card rounded-2xl bento-shadow">
                                <div className="font-display text-4xl font-bold text-primary/20 mb-2">{s.step}</div>
                                <h3 className="font-display text-lg font-semibold mb-1.5">{s.title}</h3>
                                <p className="text-sm text-muted-foreground leading-relaxed">{s.desc}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA */}
            <section className="py-16 lg:py-20">
                <div className="max-w-4xl mx-auto px-5 sm:px-8 text-center">
                    <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight">
                        Like what you see?
                    </h2>
                    <p className="text-muted-foreground mt-3 max-w-xl mx-auto">
                        Take our 60-second smile assessment and get a personalised treatment plan with cost & timeline from a certified doctor near you.
                    </p>
                    <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
                        <Link to="/smile-assessment">
                            <Button
                                size="lg"
                                className="h-12 px-7 rounded-full bg-gradient-to-r from-primary to-[#003E50] text-white font-semibold tracking-wide hover:from-[#003E50] hover:to-primary transition-all hover:scale-[1.03] shadow-lg"
                                data-testid="sim-page-cta-assessment"
                            >
                                <Sparkle size={16} weight="fill" className="mr-2" />
                                TAKE FREE SMILE ASSESSMENT
                                <ArrowRight size={16} className="ml-2" />
                            </Button>
                        </Link>
                        <Link to="/find-doctor">
                            <Button
                                size="lg"
                                variant="outline"
                                className="h-12 px-7 rounded-full border-2"
                                data-testid="sim-page-cta-find"
                            >
                                FIND A DOCTOR
                            </Button>
                        </Link>
                    </div>
                    <p className="text-[11px] text-muted-foreground mt-6">
                        Privacy first, your photo is never stored on our servers.
                    </p>
                </div>
            </section>
        </div>
    );
}
